// apps/web/src/features/clients/components/CopyFallback.tsx
//
// Shown when a clipboard write is refused (denied permission, insecure origin,
// no user gesture on Safari). The exact text is exposed as a selectable,
// read-only field so the operator can still take the requisites manually — a
// bare "copy failed" toast would be a dead end (AC-6).
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../ui/index.js';
import { useTouchActionStyle } from '../useMobileFieldStyle.js';

export interface CopyFallbackProps {
  text: string;
  onDismiss: () => void;
}

export function CopyFallback({ text, onDismiss }: CopyFallbackProps) {
  const { t } = useTranslation(['clients', 'common']);
  const ref = useRef<HTMLTextAreaElement>(null);
  const actionStyle = useTouchActionStyle();

  // Pre-select the text so the manual path is one keystroke, not a drag.
  useEffect(() => {
    ref.current?.select();
  }, [text]);

  return (
    <div
      role="alert"
      data-testid="clients-copy-fallback"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--s-2)',
        padding: 'var(--s-4)',
        border: '1px solid var(--status-error-fg)',
        background: 'var(--status-error-bg)',
        borderRadius: 'var(--r-2)',
      }}
    >
      <div style={{ fontSize: 'var(--fz-13)', color: 'var(--status-error-fg)' }}>
        {t('clients:copy.fallbackTitle')}
      </div>
      <textarea
        ref={ref}
        readOnly
        value={text}
        aria-label={t('clients:copy.fallbackLabel')}
        rows={Math.min(8, Math.max(2, text.split('\n').length))}
        style={{
          width: '100%',
          padding: 'var(--s-3) var(--s-4)',
          borderRadius: 'var(--r-2)',
          border: '1px solid var(--border-1)',
          background: 'var(--bg-1)',
          color: 'var(--fg-1)',
          fontFamily: 'var(--font-ui)',
          // 16 px keeps mobile Safari from auto-zooming this focused field.
          fontSize: 'var(--fz-16)',
          lineHeight: 1.5,
          resize: 'vertical',
        }}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button size="sm" kind="ghost" style={actionStyle} onClick={onDismiss}>
          {t('common:actions.close')}
        </Button>
      </div>
    </div>
  );
}
